import React, { useState, useCallback, useEffect,useRef } from 'react';
import { Filter, ChevronLeft, ChevronRight,Download } from 'lucide-react';
import {
 
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LabelList,
  LineChart, Line, CartesianGrid
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../context/apiClient';
import { useReactToPrint } from 'react-to-print';
import PrintView from './PrintView';

const getTodayString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const allAuditLogData = [
  { timestamp: '2025-09-25 16:13:47', status: 'PASS', policy: 'N/A', risk: '-27%' },
  { timestamp: '2025-09-25 16:13:47', status: 'PASS', policy: 'N/A', risk: '-18%' },
  { timestamp: '2025-09-25 16:13:47', status: 'PASS', policy: 'N/A', risk: '2%' },
  { timestamp: '2025-09-25 16:13:47', status: 'PASS', policy: 'N/A', risk: '-1%' },
  { timestamp: '2025-09-25 16:13:47', status: 'BLOCK', policy: 'AI-Harmful Intent', risk: '51%', reason: 'AI-Harmful Intent' },
  { timestamp: '2025-09-25 16:13:47', status: 'PASS', policy: 'N/A', risk: '-13%' },
];

// --- Reusable Components (in-file) ---

// Card component for charts and logs
const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ title }) => (
  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 px-6 py-4">
    {title}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`p-6 ${className}`}>
    {children}
  </div>
);

// --- App Components ---

function Header() {
  return (
    <header>
      
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Guardrail Activity Report</h2>
      <p className="mt-1 text-sm text-gray-600">
        Real-time guardrails for your model over a selected periods.
      </p>
    </header>
  );
}

const TruncatedText = ({ text, limit = 50 }) => {
  if (!text) return '-';
  const words = text.split(' ');
  if (words.length <= limit) {
    return text;
  }
  const truncated = words.slice(0, limit).join(' ') + '...';
  return <span title={text}>{truncated}</span>;
};


function FilterControls({ onApply }) {
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState(getTodayString());

  const handleApplyClick = () => {
    onApply({ startDate, endDate });
  };


  return (
        <section className="flex flex-col sm:flex-row sm:items-end gap-4">
      <div className="flex flex-wrap gap-4 flex-1">
        {/* Start Date */}
        <div className="flex-1 min-w-[150px]">
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full pl-3 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        {/* End Date */}
        <div className="flex-1 ">
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full pl-3 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      <button onClick={handleApplyClick} className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center gap-2">
        <Filter size={16} />
        Apply Filter
      </button>
    </section>
  );
}

function SummaryCards({ data }) {
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {data.map((item) => (
        <div key={item.label} className="bg-gray-50 border border-gray-200 p-6 rounded-lg text-center shadow-sm">
          <p className={`text-4xl font-bold ${item.color || 'text-gray-900'}`}>{item.value}</p>
          <p className="text-sm text-gray-600 mt-1">{item.label}</p>
        </div>
      ))}
    </section>
  );
}

function OverallStatusChart({ data }) {
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    // Calculate the position for the label (slightly outside the pie slice)
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5; // mid-radius
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const name = data[index].name;

    if (percent * 100 > 5) { // Only show label if slice is big enough
      return (
        <text
          x={x}
          y={y}
          fill="white"
          textAnchor="middle"
          dominantBaseline="central"
          className="text-xs font-semibold"
        >
          
          <tspan x={x} dy="1.2em">{`${(percent).toFixed(0)}`}</tspan>
        </text>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader title="Overall Status Distribution" />
      <CardContent className="flex flex-col md:flex-row items-center justify-center gap-8 py-4">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              fill="#8884d8"
              paddingAngle={2}
              dataKey="value"
              
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name, props) => [`${props.payload.percent}% (${value})`, name]} />
          </PieChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div className="space-y-2 w-full max-w-[150px]">
          {data.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <div className="flex items-center">
                <span className="w-3 h-3 rounded-sm mr-2" style={{ backgroundColor: item.color }}></span>
                <span className="font-medium" style={{ color: item.color }}>
                  {item.name}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-900 font-semibold">{item.percent}%</span>
                <span className="ml-1 text-gray-500">({item.value})</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PolicyTypeChart({ data }) {
  return (
    <Card>
      <CardHeader title="Block Policy Type Distribution" />
      <CardContent className="h-64 flex items-center justify-center">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis dataKey="name" axisLine={false} tickLine={false} padding={{ left: 20, right: 20 }} />
            <YAxis hide domain={[0, 10]} /> {/* Adjust domain based on max value */}
            <Tooltip />
            <Bar dataKey="value" radius={[5, 5, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fontWeight: 'bold' }} />
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function ActivityTrendChart({ data }) {
  return (
    <Card>
      <CardHeader title="7-Day Activity Trend (Queries vs Blocks)" />
      <CardContent className="h-80 flex items-center justify-center">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
            <XAxis dataKey="name" tickLine={false} axisLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="queries" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
            <Line type="monotone" dataKey="blocks" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function BlockReasonsChart({ data }) {
  return (
    <Card>
      <CardHeader title="Block Reasons" />
      <CardContent className="space-y-6 h-80 overflow-y-auto">
        {data.map((item) => (
          <div key={item.reason}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700">{item.reason}</span>
              <span className="text-gray-500">{item.percent}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-3">
                <div
                  className={`${item.color} h-3 rounded-full`}
                  style={{ width: `${item.percent}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-700 w-6 text-right">{item.count}</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AuditLog({ data }) {
  return (
    <Card>
      <CardHeader title="Detailed Audit Log" />
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Prompt
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Response
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Block Reason
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, idx) => (
              <tr key={idx}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{row.created_at}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    row.status === 'PASS'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {row.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs"><TruncatedText text={row.prompt} /></td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs"><TruncatedText text={row.response} /></td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs"><TruncatedText text={row.reason} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between mt-4 px-6 py-3 bg-white border-t border-gray-200">
      <div className="text-sm text-gray-700">
        Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <ChevronLeft size={16} />
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};


// --- Main App Component ---
export default function RailLogs() {
  const [displayedAuditLog, setDisplayedAuditLog] = useState(allAuditLogData);
  const [summaryData, setSummaryData] = useState([]);
  const [statusDistributionRechartsData, setStatusDistributionRechartsData] = useState([]);
  const [blockPolicyTypeData, setBlockPolicyTypeData] = useState([]);
  const [activityTrendData, setActivityTrendData] = useState([]);
  const [blockReasonsData, setBlockReasonsData] = useState([]);
  const [allAuditLogDataMaster, setAllAuditLogDataMaster] = useState(allAuditLogData);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [printData,setPrintData]=useState({});
  const [showPrintView, setShowPrintView] = useState(false);
  const [filtering, setFiltering] = useState({"startDate":"2024-01-01","endDate":getTodayString()});
  
  useEffect(() => {
    const totalRequests = displayedAuditLog.length;
    const totalBlocks = displayedAuditLog.filter((log) => log.status === 'BLOCK').length;
    const totalPasses = displayedAuditLog.filter((log) => log.status === 'PASS').length;
    const safePassRate = totalRequests > 0 ? ((totalPasses / totalRequests) * 100).toFixed(1) : '0.0';

    setSummaryData([
      { label: 'Total Requests', value: totalRequests.toString() },
      { label: 'Total Blocks', value: totalBlocks.toString() },
      {
        label: 'Safe Pass Rate',
        value: `${safePassRate}%`,
        color: 'text-green-600',
      },
    ]);

    const passCount = totalPasses;
    const blockCount = totalBlocks;
    const passPercent = totalRequests > 0 ? parseFloat(((passCount / totalRequests) * 100).toFixed(1)) : 0;
    const blockPercent = totalRequests > 0 ? parseFloat(((blockCount / totalRequests) * 100).toFixed(1)) : 0;

    setStatusDistributionRechartsData([
      { name: 'PASS', value: passCount, percent: passPercent, color: '#22c55e' },
      { name: 'BLOCK', value: blockCount, percent: blockPercent, color: '#ef4444' },
    ].filter(item => item.value > 0));


    const inputBlocks = displayedAuditLog.filter(
      (log) => log.status === 'BLOCK' && log.block_type?.toLowerCase().includes('input')
    ).length;

    const outputBlocks = displayedAuditLog.filter(
      (log) => log.status === 'BLOCK' && log.block_type?.toLowerCase().includes('output')
    ).length;

    setBlockPolicyTypeData([
      { name: 'Input', value: inputBlocks, color: '#3b82f6' },
      { name: 'Output', value: outputBlocks, color: '#3b82f6' },
    ]);

    const trendData = {};
    if (displayedAuditLog.length > 0) {
      const latestDate = new Date(
        Math.max(
          ...displayedAuditLog.map(log => new Date(log.created_at)),
        ),
      );

      for (let i = 6; i >= 0; i--) {
        const d = new Date(latestDate);
        d.setDate(d.getDate() - i);
        const key = `${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
        trendData[key] = { name: key, queries: 0, blocks: 0 };
      }

      displayedAuditLog.forEach(log => {
        const logDate = new Date(log.created_at);
        const key = `${(logDate.getMonth() + 1).toString().padStart(2, '0')}-${logDate.getDate().toString().padStart(2, '0')}`;
        if (trendData[key]) {
          trendData[key].queries += 1;
          if (log.status === 'BLOCK') {
            trendData[key].blocks += 1;
          }
        }
      });
    }
    setActivityTrendData(Object.values(trendData));

    let systemPolicyCount = 0;
    let userPolicyCount = 0;
    let inputBlockKeywordsCount = 0;
    let outputBlockKeywordsCount = 0;

    const blockedLogs = displayedAuditLog.filter(log => log.status === 'BLOCK');
    const totalBlocked = blockedLogs.length;

    blockedLogs.forEach(log => {
      if (log.metric_name !== "Custom (Red Teaming)") {
        systemPolicyCount++;
      } else {
        const reason = log.reason || '';
        const blockType = log.block_type || '';
        
        if (reason.toLowerCase().includes('keyword') && blockType === 'INPUT') {
          inputBlockKeywordsCount++;
        } else if (reason.toLowerCase().includes('keyword') && blockType === 'OUTPUT') {
          outputBlockKeywordsCount++;
        } else {
          userPolicyCount++;
        }
      }
    });

    const calculatePercent = (count) => totalBlocked > 0 ? Math.round((count / totalBlocked) * 100) : 0;

    setBlockReasonsData([
      { reason: 'System Policy', count: systemPolicyCount, percent: calculatePercent(systemPolicyCount), color: 'bg-red-500' },
      { reason: 'User Policy', count: userPolicyCount, percent: calculatePercent(userPolicyCount), color: 'bg-red-500' },
      { reason: 'Input Block Keywords', count: inputBlockKeywordsCount, percent: calculatePercent(inputBlockKeywordsCount), color: 'bg-red-500' },
      { reason: 'Output Block Keywords', count: outputBlockKeywordsCount, percent: calculatePercent(outputBlockKeywordsCount), color: 'bg-red-500' },
    ].sort((a, b) => b.count - a.count));

  }, [displayedAuditLog]);

    
  useEffect(() => {
    const fetchLogs = async () => {
      if (!user?.token) return;
      setError(null);
      try {
        const response = await authenticatedFetch('/api/rail-logs', {}, user.token, logout);
        if (!response.ok) {
          throw new Error(`API error! status: ${response.status}`);
        }
        const data = await response.json();
        setAllAuditLogDataMaster(data);
        setDisplayedAuditLog(data);
      } catch (e) {
        setError('Failed to load audit logs. Displaying dummy data.');
        console.error('Fetch logs error:', e);
        // Keep dummy data if fetch fails
      }
    };
    fetchLogs();
  }, [user, logout]);

  useEffect(() => {

      setPrintData({
        summaryData,
        statusDistributionRechartsData,
        blockPolicyTypeData,
        activityTrendData,
        blockReasonsData,
        displayedAuditLog,
        filtering,
      })
      
    }, [summaryData, statusDistributionRechartsData,blockPolicyTypeData,activityTrendData,blockReasonsData,displayedAuditLog,filtering]);

  // useEffect(() => {
  //   // When showPrintView becomes true, trigger the print action.
  //   if (showPrintView) {
  //     handlePrint();
  //   }
  // }, [showPrintView]);


  const handleApplyFilters = useCallback(({ startDate, endDate }) => {
    console.log('Applying filters:', { startDate, endDate });

    // Create Date objects for comparison. Add a day to endDate to include the whole day.
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setDate(end.getDate() + 1);

    const filteredData = allAuditLogDataMaster.filter(log => {
      const logDate = new Date(log.created_at.split(' ')[0]);
      return logDate >= start && logDate < end;
    });

    setDisplayedAuditLog(filteredData);
    setCurrentPage(1); // Reset to the first page after filtering
    setFiltering({ "startDate": startDate, "endDate": endDate });

  }, [allAuditLogDataMaster]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLogItems = displayedAuditLog.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(displayedAuditLog.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const triggerPrint = () => {
    setShowPrintView(true);
  }

  const componentRef = useRef();
  const [downloading, setDownloading] = useState(false);
  const chartsReadyRef = useRef(0);
  const TOTAL_CHARTS = 3;
  
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'Chart Report',
    //onBeforePrint: () => setDownloading(true),
    // onAfterPrint: () => {
    //   //setDownloading(false);
    //   setShowPrintView(false); // Hide the component after printing
    // },
    onBeforeGetContent: () =>
      new Promise((resolve) => {
        chartsReadyRef.current = 0; // Reset chart readiness counter
        const check = () => {
          if (chartsReadyRef.current >= TOTAL_CHARTS) resolve();
          else requestAnimationFrame(check);
        };
        check();
      }),
  });

  return (
    <div >
        <style>{`
          td {
              word-break: break-word;  /* for very long words or Unicode text */
              white-space: normal;     /* allow wrapping */
            }
        `}</style>
  
        
          <div ref={componentRef}>
              <PrintView chartsReadyRef={chartsReadyRef} printData={printData}/>
              <div className="bg-image-section"></div>
          </div>
        
       <div className="p-4 sm:p-8 max-w-[1440px] mx-auto bg-gray-50 min-h-screen space-y-6">
        {/* Main content area from the image */}
        <div className="space-y-6">
          {/* <Header /> */}

                  <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Guardrail Activity Report</h2>
            <p className="mt-2 text-sm text-gray-600">
               Real-time guardrails for your model over a selected periods.
            </p>
          </div>
          <button 
            onClick={handlePrint}
            disabled={downloading}
            className={`flex items-center gap-2 p-2 px-3 rounded border transition-colors ${
              downloading 
                ? 'bg-blue-50 border-blue-200 cursor-not-allowed' 
                : 'hover:bg-gray-100 border-transparent bg-white shadow-sm'
            }`}
          >
            <Download className={`w-4 h-4 ${downloading ? 'animate-bounce text-blue-500' : 'text-gray-600'}`} />
            <span className={`text-sm font-medium ${downloading ? 'text-blue-600' : 'text-gray-600'}`}>
              {downloading ? 'Generating PDF...' : 'Print to PDF'}
            </span>
          </button>
         </header>

          {error && (
            <div className="p-3 mb-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg text-sm">
              {error}
            </div>
          )}


          <FilterControls onApply={handleApplyFilters} />
          <SummaryCards data={summaryData} />
        </div>
        
        {/* Charts Grid */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OverallStatusChart data={statusDistributionRechartsData} />
          <PolicyTypeChart data={blockPolicyTypeData} />
          <ActivityTrendChart data={activityTrendData} />
          <BlockReasonsChart data={blockReasonsData} />
        </section>

        {/* Audit Log Table */}
       
        <Card>
          <AuditLog data={currentLogItems} />
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </Card>
      </div>
    </div>
  );
}
